import { z } from "zod"
import { getEnv } from "./envManager"
import { safeFetch } from "./safe-fetch"

const userLearningSchema = z.object({
  id: z.string(),
  langfusePromptId: z.string(),
  description: z.string(),
  content: z.string(),
  updatedAt: z.string(),
})

export type UserLearning = z.infer<typeof userLearningSchema>

const learningsResponseSchema = z.object({ learnings: z.array(userLearningSchema) })
const learningResponseSchema = z.object({ learning: userLearningSchema })

export const getUserLearnings = async (authHeader: string): Promise<UserLearning[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL")
  const response = await safeFetch(`${urlBase}/api/user-learnings`, {
    method: "GET",
    headers: { Authorization: authHeader },
  })
  if (!response.ok) throw new Error("Failed to fetch learnings")

  return learningsResponseSchema.parse(await response.json()).learnings
}

export const updateUserLearning = async (
  authHeader: string,
  id: string,
  updates: { description?: string; content?: string },
): Promise<UserLearning> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL")
  const response = await safeFetch(`${urlBase}/api/user-learnings/${id}`, {
    method: "PUT",
    body: JSON.stringify(updates),
    headers: { Authorization: authHeader, "Content-Type": "application/json" },
  })
  if (!response.ok) throw new Error("Failed to update learning")

  return learningResponseSchema.parse(await response.json()).learning
}

export const deleteUserLearning = async (authHeader: string, id: string): Promise<void> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL")
  const response = await safeFetch(`${urlBase}/api/user-learnings/${id}`, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  })
  if (!response.ok) throw new Error("Failed to delete learning")
}

// The run trigger lives on the Flask backend (`API_BASE_URL` includes `/api/v1`),
// unlike the webapp-hosted CRUD above.
export const runLearnings = async (authHeader: string): Promise<void> => {
  const urlBase = getEnv("API_BASE_URL")
  const response = await safeFetch(`${urlBase}/learnings/run`, {
    method: "POST",
    headers: { Authorization: authHeader },
  })
  if (!response.ok) throw new Error("Failed to run learnings")
}
